import { useState } from 'react';
import { useFeedback, FEEDBACK_CATEGORIES, FEEDBACK_STATUS, type FeedbackCategory } from '../../hooks/useFeedback';
import { useSystemSettings } from '../../hooks/useSystemSettings';

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

export default function FeedbackPage() {
  const { feedbacks, loading, submitFeedback } = useFeedback();
  const { settings } = useSystemSettings();
  const platformName = settings.platform_name || 'nossa equipe';

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<FeedbackCategory>('outro');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSubmitting(true);
    setResult(null);
    const res = await submitFeedback({ subject: subject.trim(), category, message: message.trim() });
    setSubmitting(false);
    if (res.success) {
      setSubject(''); setCategory('outro'); setMessage('');
      setResult({ type: 'success', msg: 'Feedback enviado! Obrigado por nos ajudar a melhorar.' });
    } else {
      setResult({ type: 'error', msg: res.error ?? 'Erro ao enviar.' });
    }
  };

  const catInfo = (val: string) => FEEDBACK_CATEGORIES.find(c => c.value === val);
  const statusInfo = (val: string) => FEEDBACK_STATUS.find(s => s.value === val);

  return (
    <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8 w-full">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-feedback-line text-emerald-500 text-xl" />
          </div>
          <div>
            <h1 className="text-[20px] sm:text-[22px] font-700 text-gray-900">Feedback</h1>
            <p className="text-[13px] text-gray-400">Privado · só você e o time veem seus envios</p>
          </div>
        </div>
        <div className="mt-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3">
          <i className="ri-shield-check-line text-emerald-500 text-base mt-0.5 shrink-0" />
          <p className="text-[13px] text-emerald-700 leading-relaxed">
            Este é um canal <strong>privado e seguro</strong> entre você e a equipe {platformName}.
            Seu feedback nos ajuda a corrigir bugs, responder dúvidas e melhorar o produto. Nenhum outro usuário tem acesso aos seus envios.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
        <h2 className="text-[15px] font-700 text-gray-800 mb-5">Enviar feedback</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category */}
          <div>
            <label className="text-[12px] font-600 text-gray-500 uppercase tracking-wide block mb-2">
              Categoria *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FEEDBACK_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-[12px] font-500 cursor-pointer transition-colors ${
                    category === cat.value
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${cat.icon} text-[14px] shrink-0`} />
                  <span className="truncate">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-[12px] font-600 text-gray-500 uppercase tracking-wide block mb-1.5">
              Assunto *
            </label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={120}
              required
              placeholder="Ex: Erro ao salvar lançamento"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400"
            />
          </div>

          {/* Message */}
          <div>
            <label className="text-[12px] font-600 text-gray-500 uppercase tracking-wide block mb-1.5">
              Mensagem *
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={1500}
              required
              rows={5}
              placeholder="Descreva com o máximo de detalhes possível. Quanto mais contexto você der, mais rápido conseguimos ajudar."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1 text-right">{message.length}/1500</p>
          </div>

          {result && (
            <div className={`px-3 py-2.5 rounded-xl text-[13px] font-500 ${
              result.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'
            }`}>
              <i className={`${result.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} mr-1.5`} />
              {result.msg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !subject.trim() || !message.trim()}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-600 rounded-xl text-[13px] cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
            {submitting ? 'Enviando...' : 'Enviar feedback'}
          </button>
        </form>
      </div>

      {/* History */}
      <div>
        <h2 className="text-[15px] font-700 text-gray-800 mb-4">Meus envios anteriores</h2>
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl py-10 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
              <i className="ri-inbox-line text-gray-400 text-lg" />
            </div>
            <p className="text-[13px] font-600 text-gray-500">Nenhum feedback enviado ainda</p>
            <p className="text-[12px] text-gray-400 mt-0.5">Use o formulário acima para enviar sua primeira mensagem</p>
          </div>
        ) : (
          <div className="space-y-3">
            {feedbacks.map(fb => {
              const cat = catInfo(fb.category);
              const st = statusInfo(fb.status);
              return (
                <div key={fb.id} className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${cat?.icon ?? 'ri-chat-1-line'} text-gray-500 text-sm`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[14px] font-700 text-gray-800 truncate pr-2">{fb.subject}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {cat?.label} · {fmtDate(fb.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`text-[11px] px-2 py-1 rounded-full font-600 whitespace-nowrap shrink-0 ${st?.cls ?? 'bg-gray-100 text-gray-500'}`}>
                      {st?.label ?? fb.status}
                    </span>
                  </div>
                  <p className="text-[13px] text-gray-600 mt-3 line-clamp-3">{fb.message}</p>
                  {fb.admin_notes && (
                    <div className="mt-3 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl">
                      <p className="text-[11px] font-700 text-emerald-600 mb-0.5 uppercase tracking-wide">Resposta da equipe</p>
                      <p className="text-[12px] text-emerald-800">{fb.admin_notes}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
